import { useMutation } from "@tanstack/react-query";
import { updateWorkout, type Api } from "api-client";
import { reaction } from "mobx";
import { useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";

import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";

import { useWorkoutEditStore } from "./WorkoutEditContext";

export function useUpdateWorkout() {
  const authenticatedNavigationContext = useAuthenticatedNavigationContext();
  const workoutEditStore = useWorkoutEditStore();

  const updateWorkoutMutation = useMutation({
    mutationFn: (input: Api.UpdateWorkout.Input) => {
      return updateWorkout(input);
    },
    onMutate: () => {
      const stopSaving = authenticatedNavigationContext.startSaving();
      return { stopSaving };
    },
    onSuccess: (_, __, context) => {
      context?.stopSaving("success");
    },
    onError: (_, __, context) => {
      context?.stopSaving("error");
    },
  });

  const debouncedWorkoutUpdate = useDebouncedCallback(
    updateWorkoutMutation.mutate,
    300,
  );

  useEffect(() => {
    const dispose = reaction(
      () => {
        return {
          path: { workoutId: workoutEditStore.workout.id },
          body: {
            name: workoutEditStore.workout.name,
            description: workoutEditStore.workout.description,
          },
        };
      },
      (editable) => {
        debouncedWorkoutUpdate(editable);
      },
    );

    return () => {
      dispose();
      debouncedWorkoutUpdate.flush();
    };
  }, [workoutEditStore]);
}
